import type { RequestFn } from '../client';
import { Delopay } from '../client';
import { Admin } from './resources/admin';
import { AdminPortal } from './resources/adminPortal';
import { AuditLogs } from './resources/auditLogs';
import { Cache } from './resources/cache';
import { CardIssuers } from './resources/cardIssuers';
import { Configs } from './resources/configs';
import { ConnectorRestrictionRules } from './resources/connectorRestrictionRules';
import { ConnectorRestrictions } from './resources/connectorRestrictions';
import { Diagnostic } from './resources/diagnostic';
import { Gsm } from './resources/gsm';
import { PlatformBilling } from './resources/platformBilling';
import { PlatformFees } from './resources/platformFees';

/**
 * Internal-only Delopay client for DeloPay staff tooling (the admin
 * control-center, audit UIs, etc.). Extends the public `Delopay` surface
 * with admin-plane resources that must **not** be discoverable from the
 * merchant-facing package entry.
 *
 * Import path: `import { DelopayInternal } from '@delopay/sdk/internal'`.
 * The merchant-facing `import { Delopay } from '@delopay/sdk'` never
 * surfaces these.
 */
export class DelopayInternal extends Delopay {
  /** Bootstrap admin endpoints — signup, signin, onboarding. */
  readonly admin: Admin;
  /** Platform-wide customer, transaction, analytics, merchant-account ops. */
  readonly adminPortal: AdminPortal;
  /** Audit log reads. */
  readonly auditLogs: AuditLogs;
  /** Backend cache invalidation. */
  readonly cache: Cache;
  /** Platform card-issuer program management. */
  readonly cardIssuers: CardIssuers;
  /** Generic platform config store. */
  readonly configs: Configs;
  /** Per-merchant connector allowlist for phased rollouts. */
  readonly connectorRestrictions: ConnectorRestrictions;
  /** Per-shop / per-project connector allow-deny rules (routing-time). */
  readonly connectorRestrictionRules: ConnectorRestrictionRules;
  /**
   * The diagnostic console — everything the router did, without a shell.
   * Cross-merchant, and gated on the staff-only `Diagnostic` permission.
   */
  readonly diagnostic: Diagnostic;
  /** GSM (Gateway Status Mapping) routing rules. */
  readonly gsm: Gsm;
  /** Admin-only ledger credits/debits against a merchant's balance. */
  readonly platformBilling: PlatformBilling;
  /** Platform-wide fee schedules assigned to merchants. */
  readonly platformFees: PlatformFees;

  constructor(...args: ConstructorParameters<typeof Delopay>) {
    super(...args);
    const request = this.request.bind(this) as RequestFn;
    this.admin = new Admin(request);
    this.adminPortal = new AdminPortal(request);
    this.auditLogs = new AuditLogs(request);
    this.cache = new Cache(request);
    this.cardIssuers = new CardIssuers(request);
    this.configs = new Configs(request);
    this.connectorRestrictions = new ConnectorRestrictions(request);
    this.connectorRestrictionRules = new ConnectorRestrictionRules(request);
    this.diagnostic = new Diagnostic(request);
    this.gsm = new Gsm(request);
    this.platformBilling = new PlatformBilling(request);
    this.platformFees = new PlatformFees(request);
  }
}
